import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { get } from 'lodash';
import { useLocation } from 'react-router-dom';
import { Redirect } from 'react-router-dom';
import { RichText } from 'prismic-reactjs';
import { ReactComponent as StrainSlider } from '../images/strain-slider.svg';
import caryophylleneIcon from '../images/strain-icons/CARYOPHYLLENE.svg';
import humuleneIcon from '../images/strain-icons/HUMULENE.svg';
import limoneneIcon from '../images/strain-icons/LIMONENE.svg';
import linaloolIcon from '../images/strain-icons/LINALOOL.svg';
import myrceneIcon from '../images/strain-icons/MYRCENE.svg';
import ocimeneIcon from '../images/strain-icons/OCIMENE.svg';
import terpinolineIcon from '../images/strain-icons/TERPINOLENE.svg';
import pineneIcon from '../images/strain-icons/PINENE.svg';
import nerolidolIcon from '../images/strain-icons/NEROLIDOL.svg';
import farneseneIcon from '../images/strain-icons/FARNESENE.svg';
import valenceneIcon from '../images/strain-icons/Valencene.svg';

import renderButtons from '../helpers/renderButtons';

export default function StrainDetailPage(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const handleLoading = props.loading;

  const whichPage = path => {
    if (path === '/404') {
      return '404';
    } else {
      return path.split('/')[2];
    }
  };

  const pagePath = whichPage(location.pathname);

  const pageLoading = useSelector(state => get(state, 'prismic.strains.loading', true));
  const page = useSelector(state => get(state, 'prismic.strains.data', false));

  const strainName = get(page, 'name[0].text');
  const strainType = get(page, 'type');
  const strainImage = get(page, 'image.url');
  const isNewStrain = get(page, 'new_strain');
  const description = get(page, 'description');
  const averageTHC = get(page, 'average_thc[0].text');
  const averageCBD = get(page, 'average_cbd[0].text');
  const flavor = get(page, 'flavor[0].text');
  const lineage = get(page, 'lineage[0].text');
  const effects = get(page, 'effects[0].text');
  const terpenes = get(page, 'terpenes[0]');

  const strainColor = type => {
    if (!type) {
      return null;
    }
    if (type === 'Sativa') {
      return '#CA3604';
    }
    if (type === 'Indica') {
      return '#284B5C';
    }
    if (type === 'Hybrid') {
      return '#789B4A';
    }
  };

  const renderTerpenes = terpenes => {
    return (
      <div className="terpene-icons">
        {terpenes.caryophyllene && <img src={caryophylleneIcon} alt="caryophyllene"></img>}
        {terpenes.humulene && <img src={humuleneIcon} alt="humulene"></img>}
        {terpenes.limonene && <img src={limoneneIcon} alt="limonene"></img>}
        {terpenes.linalool && <img src={linaloolIcon} alt="linalool"></img>}
        {terpenes.myrcene && <img src={myrceneIcon} alt="myrcene"></img>}
        {terpenes.ocimene && <img src={ocimeneIcon} alt="ocimene"></img>}
        {terpenes.pinene && <img src={pineneIcon} alt="pinene"></img>}
        {terpenes.nerolidol && <img src={nerolidolIcon} alt="nerolidol"></img>}
        {terpenes.terpinoline && <img src={terpinolineIcon} alt="terpinoline"></img>}
        {terpenes.farnesene && <img src={farneseneIcon} alt="farnesene"></img>}
        {terpenes.valencene && <img src={valenceneIcon} alt="valencene"></img>}
      </div>
    );
  };

  const strainColorStyle = {
    color: strainColor(strainType)
  };

  const strainBGStyle = {
    backgroundColor: strainColor(strainType)
  };

  useEffect(() => {
    dispatch({
      type: 'PRISMIC_LOAD_SINGLE_BY_UID',
      key: 'strains',
      id: pagePath
    });
  }, [dispatch, pagePath]);

  useEffect(() => {
    if (!pageLoading) {
      handleLoading(false);
    }
  }, [pageLoading, handleLoading]);

  const strainSliderRef = ref => {
    if (ref && strainType) {
      setTimeout(() => {
        ref.getElementById(strainType + '_Arrow').classList.add('active');
      }, 100);
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const button = [
    {
      active: true,
      cta_link: {
        uid: 'shop'
      },
      cta_text: 'Shop',
      button_variant: 'contained',
      button_color: 'secondary'
    }
  ];

  if (!page && !pageLoading) {
    return <Redirect to={'/404'} />;
  }

  return (
    !pageLoading &&
    page && (
      <div className="strain-detail-page section-padding cool-margin">
        <div className="wrapper">
          <div className="back-button" onClick={handleBack}>
            &#60; back
          </div>
          <div className="strain-name center-align mb-40">
            <h6>Strain Finder</h6>
            <h2 style={strainColorStyle}>{strainName}</h2>
          </div>
          <div className="content">
            <div className="strain-image mb-20">
              <div className="strain-image-container">
                <img src={strainImage} alt={strainName} />
                {isNewStrain && (
                  <p className="new-strain new-strain-detail">
                    <span>new new new new new new new new new</span>
                  </p>
                )}
              </div>
            </div>
            <div className="text-content">
              <div className="strain-slider">
                <StrainSlider ref={strainSliderRef} />
              </div>
              <RichText render={description} />
              {averageTHC && (
                <span className={`average ${averageCBD && 'mr-10'}`} style={strainBGStyle}>
                  Average THC {averageTHC}
                </span>
              )}

              {averageCBD && (
                <span className="average" style={strainBGStyle}>
                  Average CBD {averageCBD}
                </span>
              )}
              <h6>Flavor</h6>
              <p>{flavor}</p>
              <h6>Lineage</h6>
              <p>{lineage}</p>
              <h6>Reported Effects</h6>
              <p>{effects}</p>
              <h6>Prevalent Terpenes</h6>
              {renderTerpenes(terpenes)}
              {renderButtons(button)}
            </div>
          </div>
        </div>
      </div>
    )
  );
}
